import React, { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../../auth/firebaseAuth';
import { useNavigate } from 'react-router-dom';
import {
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  MapPinIcon,
  HomeIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc
} from 'firebase/firestore';
import { Dialog, Transition } from '@headlessui/react';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';

interface NewsletterPreference {
  id: string;
  email: string;
  userId: string;
  status: 'confirmed' | 'pending' | 'unsubscribed';
  preferences?: {
    location?: string;
    priceRange?: string;
    propertyType?: string;
    listingIntent?: string;
  };
  subscriptionDate?: any;
  subscriptionSource?: string;
}

const NewsletterPreferences: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const analytics = useAnalyticsWithConsent();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUnsubscribeModalOpen, setIsUnsubscribeModalOpen] = useState(false);
  const [subscription, setSubscription] = useState<NewsletterPreference | null>(
    null
  );
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const [preferences, setPreferences] = useState({
    location: '',
    priceRange: '',
    propertyType: '',
    listingIntent: ''
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    fetchSubscription();
  }, [currentUser, navigate]);

  const fetchSubscription = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const db = getFirestore();
      const newsletterRef = collection(db, 'newsletter');
      const q = query(
        newsletterRef,
        where('userId', '==', currentUser.uid),
        where('status', '!=', 'unsubscribed')
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data() as Omit<NewsletterPreference, 'id'>;
        setSubscription({
          ...data,
          id: doc.id
        });

        if (data.preferences) {
          setPreferences({
            location: data.preferences.location || '',
            priceRange: data.preferences.priceRange || '',
            propertyType: data.preferences.propertyType || '',
            listingIntent: data.preferences.listingIntent || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load subscription details. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    if (!subscription) return;

    setSaving(true);
    setMessage(null);

    try {
      const db = getFirestore();
      const subscriptionRef = doc(db, 'newsletter', subscription.id);

      await updateDoc(subscriptionRef, {
        preferences: {
          location: preferences.location,
          priceRange: preferences.priceRange,
          propertyType: preferences.propertyType,
          listingIntent: preferences.listingIntent
        },
        updatedAt: new Date()
      });

      analytics.trackInteraction('newsletter_preferences_updated', {
        user_id: currentUser?.uid,
        has_location: !!preferences.location,
        has_price_range: !!preferences.priceRange,
        has_property_type: !!preferences.propertyType,
        has_listing_intent: !!preferences.listingIntent
      });

      setMessage({
        type: 'success',
        text: 'Your preferences have been saved successfully!'
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save preferences. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeClick = () => {
    setIsUnsubscribeModalOpen(true);
  };

  const handleCloseUnsubscribeModal = () => {
    setIsUnsubscribeModalOpen(false);
  };

  const handleConfirmUnsubscribe = async () => {
    if (!subscription) return;

    setSaving(true);
    setMessage(null);

    try {
      const db = getFirestore();
      const subscriptionRef = doc(db, 'newsletter', subscription.id);

      await updateDoc(subscriptionRef, {
        status: 'unsubscribed',
        unsubscribedAt: new Date()
      });

      analytics.trackInteraction('newsletter_unsubscribed', {
        user_id: currentUser?.uid,
        source: 'preferences_page'
      });

      setIsUnsubscribeModalOpen(false);
      setMessage({
        type: 'success',
        text: "You have been unsubscribed successfully. We're sorry to see you go!"
      });

      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setIsUnsubscribeModalOpen(false);
      setMessage({
        type: 'error',
        text: 'Failed to unsubscribe. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading your preferences...</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <EnvelopeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              No Active Subscription
            </h2>
            <p className="text-gray-600 mb-6">
              You are not currently subscribed to our newsletter. Subscribe now
              to receive updates about new properties!
            </p>
            <button
              onClick={() => navigate('/property-tools/property-alerts')}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <BellIcon className="h-5 w-5 mr-2" />
              Subscribe to Newsletter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="flex items-center mb-4">
            <BellIcon className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">
              Newsletter Preferences
            </h1>
          </div>
          <p className="text-gray-600">
            Manage your newsletter subscription and customize what you receive.
          </p>
        </div>

        {/* Status Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 mr-2" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Subscription Status */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Subscription Status
          </h2>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <EnvelopeIcon className="h-6 w-6 text-blue-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">
                  {subscription.email}
                </p>
                <p className="text-sm text-gray-600">
                  Status:{' '}
                  <span
                    className={`font-semibold ${
                      subscription.status === 'confirmed'
                        ? 'text-green-600'
                        : 'text-yellow-600'
                    }`}
                  >
                    {subscription.status === 'confirmed'
                      ? 'Active'
                      : 'Pending Confirmation'}
                  </span>
                </p>
              </div>
            </div>
            {subscription.status === 'confirmed' && (
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            )}
          </div>
        </div>

        {/* Preferences Form */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Email Preferences
          </h2>
          <p className="text-gray-600 mb-6">
            Tell us what you're looking for, and we'll personalize your
            newsletter content.
          </p>

          <div className="space-y-6">
            {/* Location */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <MapPinIcon className="h-5 w-5 text-gray-400 mr-2" />
                Preferred Location
              </label>
              <input
                type="text"
                value={preferences.location}
                onChange={(e) =>
                  setPreferences({ ...preferences, location: e.target.value })
                }
                placeholder="e.g., Dublin, Cork, Galway"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                We'll prioritize properties in this area
              </p>
            </div>

            {/* Price Range */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                Price Range
              </label>
              <select
                value={preferences.priceRange}
                onChange={(e) =>
                  setPreferences({ ...preferences, priceRange: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Any Price</option>
                <option value="0-1000">€0 - €1,000</option>
                <option value="1000-1500">€1,000 - €1,500</option>
                <option value="1500-2000">€1,500 - €2,000</option>
                <option value="2000-2500">€2,000 - €2,500</option>
                <option value="2500-3000">€2,500 - €3,000</option>
                <option value="3000+">€3,000+</option>
              </select>
            </div>

            {/* Property Type */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <HomeIcon className="h-5 w-5 text-gray-400 mr-2" />
                Property Type
              </label>
              <select
                value={preferences.propertyType}
                onChange={(e) =>
                  setPreferences({
                    ...preferences,
                    propertyType: e.target.value
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Any Type</option>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="studio">Studio</option>
                <option value="room">Room</option>
              </select>
            </div>

            {/* Listing Intent */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <HomeIcon className="h-5 w-5 text-gray-400 mr-2" />
                Looking For
              </label>
              <select
                value={preferences.listingIntent}
                onChange={(e) =>
                  setPreferences({
                    ...preferences,
                    listingIntent: e.target.value
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Any</option>
                <option value="rent">To Rent</option>
                <option value="sale">To Buy</option>
              </select>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSavePreferences}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </button>
          </div>
        </div>

        {/* Unsubscribe Section */}
        <div className="bg-white rounded-lg shadow-md p-8 border-2 border-red-100">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Unsubscribe
          </h2>
          <p className="text-gray-600 mb-6">
            No longer want to receive our newsletter? You can unsubscribe at any
            time. You'll miss out on new property listings and market updates.
          </p>
          <button
            onClick={handleUnsubscribeClick}
            disabled={saving}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <XCircleIcon className="h-5 w-5 mr-2" />
            Unsubscribe from Newsletter
          </button>
        </div>

        {/* Info Section */}
        <div className="mt-6 bg-blue-50 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2">
            📧 What to expect
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• Weekly newsletter with top 10 newly added properties</li>
            <li>• Personalized recommendations based on your preferences</li>
            <li>• Market insights and rental tips</li>
            <li>• No spam - we value your inbox!</li>
          </ul>
        </div>
      </div>

      {/* Unsubscribe Confirmation Modal */}
      <Transition.Root show={isUnsubscribeModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={handleCloseUnsubscribeModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="p-6">
                    <div className="flex items-center justify-center mb-6">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                        <ExclamationTriangleIcon
                          className="h-6 w-6 text-red-600"
                          aria-hidden="true"
                        />
                      </div>
                    </div>

                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-center text-gray-900 mb-4"
                    >
                      Unsubscribe from Newsletter
                    </Dialog.Title>

                    <p className="text-center text-gray-600 mb-4">
                      Are you sure you want to unsubscribe from our newsletter?
                      You will no longer receive updates about new properties.
                    </p>

                    <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6">
                      <p className="text-red-600 font-medium text-center mb-2">
                        You'll miss out on:
                      </p>
                      <ul className="text-gray-600 space-y-2 text-center">
                        <li>Weekly top 10 property listings</li>
                        <li>Personalized property recommendations</li>
                        <li>Market insights and rental tips</li>
                        <li>Early access to new listings</li>
                      </ul>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-3">
                      <button
                        type="button"
                        className="w-full sm:w-32 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium focus:outline-none"
                        onClick={handleCloseUnsubscribeModal}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="w-full sm:w-40 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleConfirmUnsubscribe}
                        disabled={saving}
                      >
                        {saving ? 'Unsubscribing...' : 'Unsubscribe'}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default NewsletterPreferences;
